import React, { useId, useState } from "react";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { HiChartBar, HiTableCells } from "react-icons/hi2";
import { usePrefersReducedMotion } from "./hooks";

// Validated categorical order (brand accent, then blue): see dashboardKit.css.
export const SERIES_COLORS = ["var(--dk-series-1)", "var(--dk-series-2)"];
const SERIES_HEX = ["#c93312", "#2a78d6"];

function ChartTooltip({ active, payload, series, formatValue }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="dk-tooltip" role="status">
      <p className="dk-tooltip__title">{row.title}</p>
      {series.map((s, i) => (
        <div key={s.key} className="dk-tooltip__row">
          <span className={`dk-key dk-key--${s.kind === "bar" ? "rect" : "line"}`} style={{ background: SERIES_HEX[i] }} />
          <strong>{(s.format || formatValue)(row[s.key])}</strong>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Time-series chart card body.
 * series: [{ key, label, kind: "area" | "line" | "bar", format? }]
 * One y-axis only; series must share a unit.
 */
export default function TrendChart({ rows, series: seriesInput, formatValue = (v) => v, formatTick = (v) => v, height = 260, refreshing = false, emptyText = "No activity in this period yet.", caption, integer = false }) {
  const reduced = usePrefersReducedMotion();
  // Columns only while each bucket gets a readable width; dense daily data reads better as lines.
  const series = seriesInput.map((s) => (s.kind === "bar" && rows.length * seriesInput.length > 26 ? { ...s, kind: "line" } : s));
  const [view, setView] = useState("chart");
  const gid = useId().replace(/:/g, "");
  const isEmpty = !rows.length || rows.every((r) => series.every((s) => !r[s.key]));
  const lastIndex = rows.length - 1;
  // Small whole-number data gets fewer ticks instead of a stretched 0–4 axis.
  const peak = Math.max(0, ...rows.flatMap((r) => series.map((s) => Number(r[s.key]) || 0)));
  const tickCount = integer ? Math.max(2, Math.min(5, Math.ceil(peak) + 1)) : 5;

  return (
    <div className={`dk-chart${refreshing ? " is-refreshing" : ""}`}>
      <div className="dk-chart__bar">
        {series.length > 1 ? (
          <ul className="dk-legend" aria-label="Legend">
            {series.map((s, i) => (
              <li key={s.key}>
                <span className={`dk-key dk-key--${s.kind === "bar" ? "rect" : "line"}`} style={{ background: SERIES_HEX[i] }} />
                {s.label}
              </li>
            ))}
          </ul>
        ) : (
          <span />
        )}
        <div className="dk-toggle" role="group" aria-label="View as">
          <button type="button" className={view === "chart" ? "is-active" : ""} aria-pressed={view === "chart"} onClick={() => setView("chart")} title="Chart">
            <HiChartBar aria-hidden="true" />
            <span className="dk-sr">Chart</span>
          </button>
          <button type="button" className={view === "table" ? "is-active" : ""} aria-pressed={view === "table"} onClick={() => setView("table")} title="Table">
            <HiTableCells aria-hidden="true" />
            <span className="dk-sr">Table</span>
          </button>
        </div>
      </div>

      {view === "table" ? (
        <div className="dk-table-wrap" style={{ maxHeight: height + 24 }}>
          <table className="dk-table">
            {caption && <caption className="dk-sr">{caption}</caption>}
            <thead>
              <tr>
                <th scope="col">Period</th>
                {series.map((s) => (
                  <th key={s.key} scope="col">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.bucket}>
                  <th scope="row">{r.title}</th>
                  {series.map((s) => (
                    <td key={s.key}>{(s.format || formatValue)(r[s.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="dk-chart__plot" style={{ minHeight: height, height }}>
          {isEmpty && <p className="dk-chart__empty">{emptyText}</p>}
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 12, right: 16, bottom: 0, left: 0 }} barGap={2} barCategoryGap="28%" accessibilityLayer>
              <defs>
                {series.map((s, i) => (
                  <linearGradient key={s.key} id={`${gid}-${i}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={SERIES_HEX[i]} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={SERIES_HEX[i]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="var(--dk-grid)" />
              <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "var(--dk-baseline)" }} tick={{ fill: "var(--dk-muted)", fontSize: 12 }} minTickGap={18} interval="preserveStartEnd" />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: "var(--dk-muted)", fontSize: 12 }} tickFormatter={formatTick} width={56} allowDecimals={!integer} tickCount={tickCount} />
              <Tooltip
                cursor={series.some((s) => s.kind === "bar") ? { fill: "rgba(24, 24, 27, 0.04)" } : { stroke: "var(--dk-muted)", strokeWidth: 1 }}
                content={<ChartTooltip series={series} formatValue={formatValue} />}
                isAnimationActive={false}
              />
              {series.map((s, i) => {
                const common = { key: s.key, dataKey: s.key, name: s.label, isAnimationActive: !reduced, animationDuration: 900, animationEasing: "ease-out" };
                if (s.kind === "bar") return <Bar {...common} fill={SERIES_HEX[i]} radius={[4, 4, 0, 0]} maxBarSize={24} />;
                const dot = (props) =>
                  props.index === lastIndex && series.length === 1 ? <circle key="end" cx={props.cx} cy={props.cy} r={4.5} fill={SERIES_HEX[i]} stroke="#fff" strokeWidth={2} /> : null;
                const activeDot = { r: 5, fill: SERIES_HEX[i], stroke: "#fff", strokeWidth: 2 };
                if (s.kind === "area") return <Area {...common} type="monotone" stroke={SERIES_HEX[i]} strokeWidth={2} fill={`url(#${gid}-${i})`} dot={dot} activeDot={activeDot} />;
                return <Line {...common} type="monotone" stroke={SERIES_HEX[i]} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" dot={dot} activeDot={activeDot} />;
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
